import { useState, useEffect, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useInboundFilters, useInboundFilterTicketFields, useUpdateInboundFilters } from '@/hooks';
import { usePermissions } from '@/hooks/use-permissions';
import { TicketFieldsPicker } from '@/components/shared';
import {
  ChipInput,
  SettingsCard,
  SettingsCardSkeleton,
  SettingsDivider,
  SettingsError,
  SettingsSection,
  ToggleRow,
} from '@/modules/settings';
import type { TicketFieldValue } from '@/types';

// Lightweight format checks. Not RFC-perfect -- intent is to catch
// obvious typos ("foo", "hello world", "@", "not@an@email.com"), not
// to reject every exotic-but-valid address.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Wildcard domain: local-part@* (e.g. no-reply@*)
const WILDCARD_EMAIL_RE = /^[^\s@]+@\*$/;
const DOMAIN_RE = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;

type ListKey = 'blockedDomains' | 'blockedEmails';

const LISTS: Record<ListKey, { title: string; description: string; placeholder: string; noun: string; isValid: (v: string) => boolean; invalid: string }> = {
  blockedDomains: {
    title: 'Blocked domains',
    description: 'Every sender on these domains is blocked.',
    placeholder: 'e.g. newsletter.com',
    noun: 'domain',
    isValid: (v) => DOMAIN_RE.test(v),
    invalid: 'Not a valid domain (example: example.com)',
  },
  blockedEmails: {
    title: 'Blocked emails',
    description: 'Only these exact addresses are blocked.',
    placeholder: 'e.g. alerts@vendor.com',
    noun: 'email',
    isValid: (v) => EMAIL_RE.test(v) || WILDCARD_EMAIL_RE.test(v),
    invalid: 'Not a valid email address (example: name@example.com or no-reply@*)',
  },
};

interface InboundFiltersProps {
  /** Extra rows for the "Blocked senders" card, under auto-close (Ingestion puts the assignment rule there). */
  children?: ReactNode;
}

/**
 * Ingestion › Inbound: blocked senders. Messages from blocked domains or emails skip the AI
 * pipeline entirely. Every change saves straight away.
 */
export default function InboundFilters({ children }: InboundFiltersProps) {
  const { data, isLoading, error } = useInboundFilters();
  const updateMutation = useUpdateInboundFilters();
  const { data: helpdeskFields } = useInboundFilterTicketFields();
  const { can } = usePermissions();
  const canEdit = can('edit', 'spam-filter');

  // Local state mirrors server data so edits aren't lost between renders
  const [enabled, setEnabled] = useState(false);
  const [autoClose, setAutoClose] = useState(false);
  const [ticketFields, setTicketFields] = useState<TicketFieldValue[]>([]);
  const [lists, setLists] = useState<Record<ListKey, string[]>>({ blockedDomains: [], blockedEmails: [] });

  // Sync local state when server data loads/changes
  useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      setAutoClose(data.autoClose ?? false);
      setTicketFields(data.autoCloseTicketFields ?? []);
      setLists({ blockedDomains: data.blockedDomains, blockedEmails: data.blockedEmails });
    }
  }, [data]);

  const handleToggleEnabled = async (checked: boolean) => {
    setEnabled(checked);
    try {
      await updateMutation.mutateAsync({ enabled: checked });
      toast.success(checked ? 'Sender filtering enabled' : 'Sender filtering disabled');
    } catch {
      setEnabled(!checked); // rollback
      toast.error('Failed to update');
    }
  };

  const handleToggleAutoClose = async (checked: boolean) => {
    setAutoClose(checked);
    try {
      await updateMutation.mutateAsync({ autoClose: checked });
      toast.success(checked ? 'Auto-close enabled' : 'Auto-close disabled');
    } catch {
      setAutoClose(!checked); // rollback
      toast.error('Failed to update');
    }
  };

  const handleChangeTicketFields = async (updated: TicketFieldValue[]) => {
    try {
      await updateMutation.mutateAsync({ autoCloseTicketFields: updated });
      setTicketFields(updated);
      toast.success('Ticket fields updated');
    } catch {
      toast.error('Failed to update');
    }
  };

  // Gorgias only: the fields are offered when its helpdesk is connected and they can be read.
  const fieldsToOffer = (autoClose && helpdeskFields?.available && helpdeskFields.fields) || [];
  const needsReconnect = autoClose && Boolean(helpdeskFields?.needsReconnect);

  const saveList = async (key: ListKey, updated: string[], done: string): Promise<boolean> => {
    try {
      await updateMutation.mutateAsync({ [key]: updated });
      setLists((prev) => ({ ...prev, [key]: updated }));
      toast.success(done);
      return true;
    } catch {
      toast.error('Failed to update');
      return false;
    }
  };

  const handleAdd = async (key: ListKey, raw: string): Promise<boolean> => {
    const value = raw.toLowerCase();
    const list = LISTS[key];
    if (!list.isValid(value)) {
      toast.error(list.invalid);
      return false;
    }
    if (lists[key].includes(value)) {
      toast.info('Already in the list');
      return false;
    }
    return saveList(key, [...lists[key], value], `Added ${list.noun}: ${value}`);
  };

  const handleRemove = (key: ListKey, value: string) =>
    void saveList(key, lists[key].filter((v) => v !== value), `Removed ${LISTS[key].noun}: ${value}`);

  // `children` are stored elsewhere (ai_settings), so they stay usable when the filters fail; their leading divider is dropped.
  if (error)
    return (
      <SettingsSection label='Blocked senders'>
        <SettingsError>Failed to load inbound filters. Please try again later.</SettingsError>
        {children && <SettingsCard className='[&>:first-child]:hidden'>{children}</SettingsCard>}
      </SettingsSection>
    );

  const listsDisabled = !enabled || !canEdit || updateMutation.isPending;

  return (
    <SettingsSection label='Blocked senders'>
      {isLoading ? (
        <SettingsCardSkeleton rows={3} />
      ) : (
        <SettingsCard>
          <ToggleRow
            title='Enable sender filtering'
            description='Messages from blocked senders skip the AI pipeline. Useful for marketing mail, notifications and other automated senders.'
            checked={enabled}
            onCheckedChange={(checked) => void handleToggleEnabled(checked)}
            disabled={updateMutation.isPending || !canEdit}
          />
          <SettingsDivider />
          <ToggleRow
            title='Auto-close blocked tickets'
            description={autoClose ? 'Blocked tickets are closed and marked as spam.' : 'When off, blocked tickets are only marked as spam.'}
            checked={autoClose}
            onCheckedChange={(checked) => void handleToggleAutoClose(checked)}
            disabled={!enabled || !canEdit || updateMutation.isPending}
          />
          {/* Gorgias will not close a ticket while a required ticket field is empty, so auto-close has to fill them in. */}
          {(fieldsToOffer.length > 0 || needsReconnect) && (
            <div className='flex flex-col gap-3 rounded-2xl border border-gray-300 bg-gray-50 p-4'>
              <ToggleRow
                title='Ticket fields to set when auto-closing'
                description={
                  needsReconnect
                    ? 'Gorgias will not close a ticket while a required ticket field is empty. Reconnect Gorgias so its ticket fields can be listed here.'
                    : 'Gorgias will not close a ticket while a required ticket field is empty. Choose the value auto-close fills in for each one.'
                }
              />
              {fieldsToOffer.length > 0 && (
                <TicketFieldsPicker
                  fields={fieldsToOffer}
                  value={ticketFields}
                  onChange={(updated) => void handleChangeTicketFields(updated)}
                  disabled={!enabled || !canEdit || updateMutation.isPending}
                />
              )}
            </div>
          )}
          {children}
        </SettingsCard>
      )}
      {(Object.keys(LISTS) as ListKey[]).map((key) =>
        isLoading ? (
          <SettingsCardSkeleton key={key} rows={1} />
        ) : (
          <SettingsCard key={key}>
            <ToggleRow title={LISTS[key].title} description={LISTS[key].description} />
            <ChipInput
              values={lists[key]}
              onAdd={(value) => handleAdd(key, value)}
              onRemove={(value) => handleRemove(key, value)}
              placeholder={LISTS[key].placeholder}
              disabled={listsDisabled}
              withAddButton
            />
          </SettingsCard>
        )
      )}
    </SettingsSection>
  );
}
